import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import OnboardingWizard from "@/components/OnboardingWizard";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import QuickStartGuide from "@/components/QuickStartGuide";
import {
  ScanLine, CalendarDays, FileSpreadsheet, BedDouble,
  Users, ArrowRight, TrendingUp, ShieldCheck,
} from "lucide-react";

/** Fade-in-up motion presets shared by the tiles below. */
const FADE_UP = {
  hidden: { opacity: 0, y: 12 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};
const STAGGER = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };

/**
 * Static config for the 4 primary dashboard actions. i18n labels are
 * resolved at render time; keeping the rest immutable avoids re-creating
 * the array on every render.
 */
const ACTION_CONFIGS = [
  {
    to: "/app/scanner",
    icon: ScanLine,
    titleKey: "dashboard.act_scan",
    subKey:   "dashboard.act_scan_sub",
    testid:   "action-scan",
    color: "bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-[0_10px_30px_-8px_rgba(37,99,235,0.5)]",
  },
  {
    to: "/app/calendar",
    icon: CalendarDays,
    titleKey: "dashboard.act_booking",
    subKey:   "dashboard.act_booking_sub",
    testid:   "action-booking",
    color: "bg-slate-900 hover:bg-slate-800 text-white",
  },
  {
    to: "/app/calendar",
    icon: BedDouble,
    titleKey: "dashboard.act_calendar",
    subKey:   "dashboard.act_calendar_sub",
    testid:   "action-calendar",
    color: "bg-white hover:bg-stone-50 text-slate-900 border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)]",
  },
  {
    to: "/app/alloggiati",
    icon: FileSpreadsheet,
    titleKey: "dashboard.act_alloggiati",
    subKey:   "dashboard.act_alloggiati_sub",
    testid:   "action-alloggiati",
    color: "bg-white hover:bg-stone-50 text-slate-900 border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)]",
  },
];

/**
 * Dashboard "semplice per tutti": 4 maxi-pulsanti in alto + KPI essenziali.
 * Disegnata per essere usabile anche da chi non è abituato ai computer.
 */
export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { current: property } = useProperty();
  const [stats, setStats] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!property) return;
    api.get("/stats/overview").then(r => setStats(r.data)).catch(() => {});
  }, [property]);

  const ACTIONS = ACTION_CONFIGS.map(a => ({
    ...a,
    title: t(a.titleKey),
    sub: t(a.subKey),
  }));

  const firstName = user?.full_name?.split(" ")[0] || t("dashboard.greeting_default");
  const locale = i18n.language?.startsWith("en") ? "en-US" : `${i18n.language || "it"}-${i18n.language === "en" ? "US" : "IT"}`;

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <OnboardingWizard />
      <QuickStartGuide />

      <motion.div
        className="mb-10"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-blue-600 mb-2">{t("dashboard.label")}</p>
        <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
          <Trans
            i18nKey="dashboard.greeting"
            values={{ name: firstName }}
            components={{ 1: <span className="text-blue-600" /> }}
          />
        </h1>
        <p className="text-slate-500 mt-2 text-lg">
          {property ? (
            <Trans
              i18nKey="dashboard.managing"
              values={{ name: property.name, city: property.comune }}
              components={{ strong: <strong className="text-slate-900" /> }}
            />
          ) : t("dashboard.no_property")}
        </p>
        {/* Small security-trust cue right below the greeting */}
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
          {t("dashboard.security_cue", "Dati protetti · Alloggiati Web certificato · GDPR")}
        </div>
      </motion.div>

      {property && <OnboardingChecklist propertyName={property?.name} />}

      <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">{t("dashboard.actions_title")}</p>
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12"
        variants={STAGGER}
        initial="hidden"
        animate="show"
      >
        {ACTIONS.map((a) => (
          <motion.button
            variants={FADE_UP}
            key={a.title}
            onClick={() => nav(a.to)}
            data-testid={a.testid}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
            className={`${a.color} p-7 text-left rounded-2xl transition-all group flex items-start gap-5`}
          >
            <a.icon className="w-12 h-12 flex-shrink-0 mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-heading font-semibold text-2xl tracking-tight mb-1">{a.title}</div>
              <div className="text-sm opacity-90">{a.sub}</div>
            </div>
            <ArrowRight className="w-6 h-6 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
          </motion.button>
        ))}
      </motion.div>

      <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">{t("dashboard.at_glance")}</p>

      {/* Occupancy mensile: card dedicata grande con barra di avanzamento */}
      {stats && (
        <OccupancyMonthCard stats={stats} locale={locale} />
      )}

      <motion.div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        variants={STAGGER}
        initial="hidden"
        animate="show"
      >
        <SimpleCell
          label={t("dashboard.cell_free")}
          value={`${(stats?.rooms_total ?? 0) - (stats?.active_today ?? 0)}`}
          sub={t("dashboard.cell_free_sub", { total: stats?.rooms_total ?? 0 })}
          testid="stat-free" icon={BedDouble}
        />
        <SimpleCell
          label={t("dashboard.cell_guests")} value={stats?.guests_total ?? 0}
          sub={t("dashboard.cell_guests_sub")} testid="stat-guests" icon={Users}
        />
        <SimpleCell
          label={t("dashboard.cell_revenue")}
          value={`€ ${(stats?.revenue_month ?? 0).toLocaleString(locale)}`}
          sub={t("dashboard.cell_revenue_sub")} testid="stat-revenue" icon={TrendingUp} highlight
        />
        <SimpleCell
          label={t("dashboard.cell_pending")} value={stats?.pending_alloggiati ?? 0}
          sub={t("dashboard.cell_pending_sub")} testid="stat-pending" icon={FileSpreadsheet}
          warn={(stats?.pending_alloggiati ?? 0) > 0}
        />
      </motion.div>
    </div>
  );
}

function SimpleCell({ label, value, sub, testid, icon: Icon, highlight, warn }) {  let cls = "border border-slate-200 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)]";
  let iconCls = "text-slate-400";
  if (highlight) { cls = "border border-blue-200 bg-gradient-to-br from-blue-50 to-white shadow-[0_2px_20px_rgba(37,99,235,0.08)]"; iconCls = "text-blue-600"; }
  if (warn)      { cls = "border border-amber-300 bg-gradient-to-br from-amber-50 to-white shadow-[0_2px_20px_rgba(217,119,6,0.1)]"; iconCls = "text-amber-600"; }
  return (
    <motion.div
      variants={FADE_UP}
      whileHover={{ y: -2 }}
      className={`${cls} p-6 rounded-2xl transition-all`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${iconCls}`} strokeWidth={2} />}
      </div>
      <div className="font-heading font-bold text-4xl tracking-tight text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
    </motion.div>
  );
}

function OccupancyMonthCard({ stats, locale }) {
  const pct = stats.occupancy_month ?? 0;
  const occupied = stats.occupied_nights_month ?? 0;
  const available = stats.available_nights_month ?? 0;
  const freeNights = Math.max(0, available - occupied);

  // Soglia: < 40% → promo consigliata (amber), 40-70% → ok (blue), > 70% → ottimo (emerald)
  let tone, hint, barClass;
  if (pct < 40) {
    tone = { card: "border-amber-300 bg-gradient-to-br from-amber-50 to-white", text: "text-amber-700" };
    hint = `Occupazione bassa · ti conviene lanciare una promozione (${freeNights} notti libere questo mese)`;
    barClass = "bg-amber-500";
  } else if (pct < 70) {
    tone = { card: "border-blue-200 bg-gradient-to-br from-blue-50 to-white", text: "text-blue-700" };
    hint = `${freeNights} notti ancora libere · stai andando bene`;
    barClass = "bg-blue-600";
  } else {
    tone = { card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white", text: "text-emerald-700" };
    hint = `Ottima occupazione · solo ${freeNights} notti libere questo mese`;
    barClass = "bg-emerald-600";
  }

  return (
    <motion.div
      variants={FADE_UP}
      initial="hidden"
      animate="show"
      className={`border ${tone.card} shadow-[0_2px_20px_rgba(0,0,0,0.04)] p-6 rounded-2xl mb-4`}
      data-testid="stat-occupancy-month"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-slate-500">Occupazione del mese</div>
          <div className="flex items-end gap-2 mt-1">
            <div className="font-heading font-bold text-5xl tracking-tight text-slate-900">{pct.toLocaleString(locale, { maximumFractionDigits: 1 })}%</div>
            <div className={`text-sm font-medium ${tone.text} pb-2`}>
              {occupied} / {available} notti
            </div>
          </div>
        </div>
        <TrendingUp className={`w-6 h-6 ${tone.text}`} strokeWidth={2} />
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" aria-label="Occupancy progress">
        <motion.div
          className={`h-full ${barClass} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, pct)}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <div className={`text-xs ${tone.text} mt-3`}>{hint}</div>
    </motion.div>
  );
}

